import 'vite/modulepreload-polyfill';
import { createApp, onBeforeUnmount, onMounted, ref } from 'vue';

import * as AnalyticsData from '~/analytics/analytics-data';
import { useMasterProductCard } from '~/composables/use-master-product-card';
import { useScrollLock } from '~/composables/use-scroll-lock';
import { useViewportSize } from '~/composables/use-viewport-size';
import { FILTER_PARAMS, SORT_OPTIONS } from '~/constants/filtering';
import {
  TRACK_COLLECTION_FILTER_TYPE,
  TRACK_GENERIC_CTA_CLICKED_EVENT,
} from '~/constants/rudderstack';
import * as DataDogLogs from '~/lib/datadog/datadog';
import { rerenderSectionsByType } from '~/utils/section-rerenderer';
import { clearAllFilters, clearPriceFilter, updateHistory, updateUrlWithObject } from '~/utils/url';

import { BaseSection } from './base-section';

const SECTION_NAME = 'collection-product-grid';

const triggerTypes = {
  FILTER: 'filter',
  SORT: 'sort',
};

/**
 * Collection grid component for handling product sorting and filtering
 */
class CollectionProductGrid extends BaseSection {
  constructor() {
    super(`[data-section-type="${SECTION_NAME}"]`);
  }

  init(instanceData) {
    const container = instanceData.container;

    if (!container) {
      DataDogLogs.error(
        '[Allbirds section] - Collection Product Grid - init - Container not found',
        {},
        new Error('Container not found'),
      );
      return;
    }

    createApp({
      name: 'CollectionProductGridSection',
      setup() {
        const {
          cardRefs,
          getVariantDescription,
          getVariantLabel,
          handleAddToCart,
          initializeAllCards,
          setActiveColorway,
          setActiveVariant,
          toggleSizeSelector,
        } = useMasterProductCard();
        const { isMobile } = useViewportSize();
        const { lockScroll, unlockScroll } = useScrollLock();

        const showFilterDropdown = ref(false);
        const activeFilterDropdowns = ref(new Map());
        const filterDropdownRef = ref(null);
        const filterButtonsRef = ref([]);
        const isFiltering = ref(false);

        const url = new URL(window.location.href);
        const sortBy = ref(url.searchParams.get(FILTER_PARAMS.SORT_BY) || SORT_OPTIONS.MANUAL);

        /**
         * Handles sort by changes using section rendering API instead of page reload
         */
        const handleSortByChange = async (event) => {
          const newSortBy = event.target.value;
          sortBy.value = newSortBy;
          url.searchParams.set(FILTER_PARAMS.SORT_BY, newSortBy);

          // Update browser history without page reload
          updateHistory({
            pathname: window.location.pathname,
            searchParams: Object.fromEntries(url.searchParams),
          });

          const featureName = newSortBy?.toUpperCase();
          const featureLinkDestination = `${window.location.pathname}?sort_by=${newSortBy}`;
          const featureType = TRACK_COLLECTION_FILTER_TYPE.SORTING.KEY;

          // Rerender sections using the API
          await rerenderSectionsForFilterChange({
            featureLinkDestination,
            featureName,
            featureType,
            triggerType: triggerTypes.SORT,
          });
          closeFilterDropdown();
        };

        /**
         * Handles filter form submission using section rendering API
         */
        const handleFilterSubmit = async (event) => {
          event.preventDefault();

          if (isFiltering.value) return; // Prevent multiple simultaneous requests

          isFiltering.value = true;

          try {
            // Find the form element - event.target might be an input, so we need to find the parent form
            const formElement = event.target.closest('form') || event.target;
            const formData = new FormData(formElement);
            const searchParams = new URLSearchParams();

            // Convert form data to search params (append to preserve multi-select values)
            for (const [key, value] of formData.entries()) {
              if (value) {
                searchParams.append(key, value);
              }
            }

            // Update URL with new filter params
            const newUrl = new URL(window.location.href);
            newUrl.search = searchParams.toString();

            updateUrlWithObject(newUrl, true);

            // Rerender sections
            await rerenderSectionsForFilterChange({
              triggerType: triggerTypes.FILTER,
            });

            // Close filter dropdown after successful filter
            closeFilterDropdown();
          } catch (error) {
            DataDogLogs.error(
              '[Allbirds section] - CollectionProductGrid - Failed to apply filters',
              {},
              error,
            );
          } finally {
            isFiltering.value = false;
          }
        };

        /**
         * Handles clearing individual filters using section rendering API
         */
        const handleFilterClear = async (event, filterUrl) => {
          event.preventDefault();

          if (isFiltering.value) return;

          isFiltering.value = true;

          try {
            // Handle relative URLs by creating a full URL
            const url = new URL(filterUrl, window.location.origin);

            // Update browser history and URL
            window.history.pushState({}, '', url.toString());

            // Rerender sections
            await rerenderSectionsForFilterChange({
              triggerType: triggerTypes.FILTER,
            });
            closeFilterDropdown();
          } catch (error) {
            DataDogLogs.error(
              '[Allbirds section] - CollectionProductGrid - Failed to clear filter',
              {},
              error,
            );
          } finally {
            isFiltering.value = false;
          }
        };

        /**
         * Handles clearing all filters using section rendering API
         */
        const handleClearAllFilters = async (event) => {
          event.preventDefault();

          if (isFiltering.value) return;
          isFiltering.value = true;

          try {
            const url = clearAllFilters([FILTER_PARAMS.SORT_BY]);

            // Update browser history and URL
            updateUrlWithObject(url);

            // Rerender sections
            await rerenderSectionsForFilterChange({
              triggerType: triggerTypes.FILTER,
            });
            closeFilterDropdown();
          } catch (error) {
            DataDogLogs.error(
              '[Allbirds section] - CollectionProductGrid - Failed to clear all filters',
              {},
              error,
            );
          } finally {
            isFiltering.value = false;
          }
        };

        /**
         * Handles clearing the price filter while preserving other active filters
         */
        const handleClearPriceFilter = async (event) => {
          event.preventDefault();

          if (isFiltering.value) return;
          isFiltering.value = true;

          try {
            // Use the composable to clear price filter parameters
            const url = clearPriceFilter();

            // Update browser history and URL
            updateUrlWithObject(url);

            // Rerender sections
            await rerenderSectionsForFilterChange({
              triggerType: triggerTypes.FILTER,
            });
            closeFilterDropdown();
          } catch (error) {
            DataDogLogs.error(
              '[Allbirds section] - CollectionProductGrid - Failed to clear price filter',
              {},
              error,
            );
          } finally {
            isFiltering.value = false;
          }
        };

        /**
         * Rerenders relevant sections when filters change
         */
        const rerenderSectionsForFilterChange = async ({
          featureLinkDestination = null,
          featureName = null,
          featureType = null,
          triggerType = null,
        }) => {
          try {
            await rerenderSectionsByType(
              ['collection-product-grid', 'collection-header'],
              {
                preserveScroll: true,
              },
              () => {
                // Reinitialize the section after rerender
                if (window.BaseSection && typeof window.BaseSection.getInstance === 'function') {
                  const sectionId = container.getAttribute('data-section-id');
                  const instanceData = window.BaseSection.getInstance(sectionId);

                  if (instanceData && instanceData.section) {
                    instanceData.initialized = false;
                    instanceData.section._initInstance(instanceData);
                  }
                }

                if (triggerType) {
                  // Update the product object with the latest data from the section
                  const collectionObject = JSON.parse(
                    container.getAttribute('data-collection-object'),
                  );

                  const collectionPage = window?.ALLBIRDS?.page?.collectionPage?.collection;
                  window.ALLBIRDS.page.collectionPage.collection = {
                    ...collectionPage,
                    ...collectionObject,
                  };

                  switch (triggerType) {
                    case triggerTypes.FILTER:
                      AnalyticsData.triggerProductListFiltered();
                      break;
                    case triggerTypes.SORT:
                      AnalyticsData.triggerProductListFeatureClicked({
                        collection: collectionObject,
                        featureLinkDestination,
                        featureName,
                        featureType,
                      });
                      break;
                  }
                }
              },
              (error) => {
                DataDogLogs.error(
                  '[Allbirds section] - CollectionProductGrid - Failed to rerender sections',
                  {},
                  error,
                );
              },
            );
          } catch (error) {
            DataDogLogs.error(
              '[Allbirds section] - CollectionProductGrid - Error in rerenderSectionsForFilterChange',
              {},
              error,
            );
          }
        };

        const toggleFilterDropdown = (event, filterParamName) => {
          activeFilterDropdowns.value.set(
            filterParamName,
            !activeFilterDropdowns.value.get(filterParamName),
          );
        };

        const toggleFilterMenu = (event) => {
          if (!isMobile.value) {
            event.preventDefault();
            event.stopPropagation();
          }
        };

        const handleFilterDropdownChange = () => {
          showFilterDropdown.value = !showFilterDropdown.value;
          if (isMobile.value && showFilterDropdown.value) {
            lockScroll();
          }
        };

        const closeFilterDropdown = (event) => {
          if (event) {
            event.preventDefault();
          }
          showFilterDropdown.value = false;
          if (isMobile.value) {
            unlockScroll();
          }
        };

        const handleClickOutside = (event) => {
          if (
            showFilterDropdown.value &&
            filterDropdownRef.value &&
            !filterDropdownRef.value.contains(event.target) &&
            !filterButtonsRef.value.some((button) => button.contains(event.target))
          ) {
            closeFilterDropdown();
          }
        };

        onMounted(() => {
          filterDropdownRef.value = container.querySelector('[data-filter-dropdown]');
          filterButtonsRef.value = [
            container.querySelector('[data-filter-and-sort-desktop]'),
            container.querySelector('[data-filter-and-sort-mobile]'),
          ].filter(Boolean);
          document.addEventListener('click', handleClickOutside);

          // Initialize product cards with default data
          initializeAllCards(container);

          // Analytics, decorate <a> elements with Query String API event 'Product List Clicked'
          AnalyticsData.hookProductListClicked({
            collection: window.ALLBIRDS.page.collectionPage.collection,
            sectionName: SECTION_NAME,
          });

          // Analytics, decorate <a> elements with Query String API event 'Product List Feature Clicked'
          AnalyticsData.hookProductListFeatureClicked({
            collection: window.ALLBIRDS.page.collectionPage.collection,
            featureType: TRACK_COLLECTION_FILTER_TYPE.PILL_TOGGLE.KEY,
            sectionName: SECTION_NAME,
          });

          // Analytics, decorate <a> elements with Query String API event 'Call to Action Clicked'
          AnalyticsData.hookCallToActionClicked({
            locationParam: TRACK_GENERIC_CTA_CLICKED_EVENT.LOCATIONS.COLLECTION,
            sectionName: SECTION_NAME,
            typeParam: TRACK_GENERIC_CTA_CLICKED_EVENT.TYPES.LINK,
            valueParam: window.ALLBIRDS.page.collectionPage.collection?.url || '',
          });
        });

        onBeforeUnmount(() => {
          document.removeEventListener('click', handleClickOutside);
        });

        return {
          activeFilterDropdowns,
          cardRefs,
          closeFilterDropdown,
          getVariantDescription,
          getVariantLabel,
          handleAddToCart,
          handleClearAllFilters,
          handleClearPriceFilter,
          handleFilterClear,
          handleFilterDropdownChange,
          handleFilterSubmit,
          handleSortByChange,
          isFiltering,
          setActiveColorway,
          setActiveVariant,
          showFilterDropdown,
          sortBy,
          toggleFilterDropdown,
          toggleFilterMenu,
          toggleSizeSelector,
        };
      },
    }).mount(container);
  }
}

new CollectionProductGrid();
